import React, { memo, useMemo, useState } from 'react';
import { Box, FormHelperText, TextField, Typography } from '@mui/material';

interface DefaultInputProps {
  label: string;
  initialValue: string;
  placeholder?: string;
  onInputValueChange?: (newValue: string) => void;
  target?: number;
  onMultiInputValueChange?: (target: number, newValue: string) => void;
  validationFn?: (value: string) => boolean;
  sx?: any;
}

const DefaultInput = memo(
  ({
    initialValue,
    label,
    placeholder,
    onInputValueChange,
    target,
    onMultiInputValueChange,
    validationFn,
    sx
  }: DefaultInputProps) => {
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [value, setValue] = useState<string>(initialValue);
    const [error, setError] = useState<string | null>(null);
    const [currentLabel, setCurrentLabel] = useState<string[] | string>(label.split(' ', 2));

    const handleChangeValue = (e: any) => {
      setValue(e.target.value);
      onInputValueChange && onInputValueChange(e.target.value);
      (onMultiInputValueChange && target) && onMultiInputValueChange(target, e.target.value)

      if (validationFn && !validationFn?.(e.target.value)) {
        setError(`Invalid ${placeholder} format`);
      } else {
        setError(null);
      }
    };

    const isValid = useMemo(() => {
      return validationFn && isDirty ? validationFn(value) : true;
    }, [validationFn, value]);

    const labelValidationStyle = useMemo(() => {
      let style = {
        backgroundColor: '#F3F3F3',
        color: '#1F1F1F',
      };

      if (isDirty && !isValid) {
        style = {
          backgroundColor: '#FFF6F9',
          color: '#BF295C',
        };
      } else if (!isValid) {
        style = {
          backgroundColor: '#FFE4E6',
          color: '#D62F39',
        };
      }

      return style;
    }, [isValid, isDirty]);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #D6D6DB',
          flex: 1,
          ...sx,
        }}
      >
        <Box
          sx={{
            padding: '16px',
            minWidth: 192,
            borderTopLeftRadius: sx?.borderTopLeftRadius,
            borderBottomLeftRadius: sx?.borderBottomLeftRadius,
            ...labelValidationStyle,
          }}
        >
          <Typography
            sx={{
              fontWeight: '500',
            }}
          >
            {currentLabel.length > 1 ? `${label}` : `${label}`}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column-reverse',
            position: 'relative',
          }}
        >
          <TextField
            placeholder={placeholder ? `Enter ${placeholder}` : `Enter ${label} name`}
            onFocus={() => validationFn && setIsDirty(true)}
            onBlur={() => validationFn && setIsDirty(false)}
            onChange={handleChangeValue}
            defaultValue={initialValue}
            sx={{
              flex: 1,
              color: '#080808',
              fontSize: '16px',
              '&::placeholder': {
                color: '#828282',
              },
              '& fieldset': { border: 'none' },
            }}
          />
          <FormHelperText
            sx={{
              display: `${error === null ? 'none' : 'block'}`,
              marginBlockStart: 0,
              marginBlockEnd: 0,
              position: 'absolute',
              top: 0,
              left: '14px',
              color: '#D62F39',
            }}
          >
            {`${error}`}
          </FormHelperText>
        </Box>
      </Box>
    );
  },
);
export { DefaultInput };
